/**
 * useFeedbackUrlState — sibling hook layered on top of react-router's
 * `useSearchParams` for Feedback-only URL state (Plan 04-07 Task 1).
 *
 * Why a SEPARATE hook instead of extending `useUrlFilters`:
 *
 *   `useUrlFilters.filtersEqual` (use-url-filters.ts) compares a fixed keyset
 *   — preset, from, to, role, cohort, channel, granularity, users, sort.
 *   Adding feedback-only params (sort='triage', limit=50, offset=0, …) to
 *   the `Filters` shape would pollute that comparator: because feedback
 *   defaults *differ from unset*, Overview's `dirty` computation would
 *   become unstable whenever the URL carried feedback params.
 *
 *   Resolution (WARNING-08): expose feedback-only URL state through a sibling
 *   hook that uses `useSearchParams` directly. `use-url-filters.ts` stays
 *   untouched — its `Filters` keyset and `filtersEqual` comparator retain
 *   their fixed shape.
 *
 * Surface mirrors `useUrlFilters`: committed state (`sort`, `limit`, …), a
 * pending buffer with `setFilters`, `apply()` to commit, `reset()` to clear,
 * and a local `dirty` flag that's ISOLATED from `useUrlFilters.dirty`.
 *
 * Invariant: `apply()` / `reset()` use the functional `setSearchParams(prev
 * => …)` form so global-filter keys (role, cohort, …) from `useUrlFilters`
 * survive every feedback-param write.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EXPANDED_MAX } from '@/lib/expanded-rows-cap';
import type { FeedbackSort, FeedbackSource } from '@/services/api-types';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** URL keys owned by this hook. */
const FEEDBACK_URL_KEYS = [
  'sort',
  'limit',
  'offset',
  'minRating',
  'maxRating',
  'hasText',
  'userId',
  // K2 — source partition (all / chat / brief).
  'source',
  // Phase 5 (dashboard-trace-ux) — comma-joined trace IDs whose detail
  // panels are inline-expanded on the FeedbackPage. Capped at
  // `EXPANDED_MAX` to keep the URL short and prevent runaway list-bloat
  // (the panels each fetch their own trace detail on mount).
  'expanded',
] as const;

/**
 * Phase 5 — cap on the number of simultaneously expanded rows tracked in
 * the URL. Definition + rationale moved to `@/lib/expanded-rows-cap` so
 * both `useFeedbackUrlState` and `useActivityUrlState` (Phase 8 / Plan
 * 08-03) read the same constant. Re-exported here for backward compatibility
 * with any consumer that previously imported `EXPANDED_MAX` from this hook.
 */
export { EXPANDED_MAX };

// BUG-B (v1.4 dashboard-ux-fix): Latency / Cost column sort appeared broken
// because this allow-list only carried the four "bulk" sort modes. The server
// enum + FeedbackSort type already include `latency_{asc,desc}` and
// `cost_{asc,desc}` (see api-types.ts and feedbackOrderBy in queries.ts); the
// SortHeader committed e.g. `sort=latency_desc` to the URL, but the next tick
// `parseSort` dropped the value back to `'triage'` because it wasn't listed
// here. The URL looked right but `feedback.sort` was `'triage'`, so the
// SortHeader rendered no arrow and the API refetched with triage — making the
// column click look like a no-op. Keep this list in lock-step with the
// `FeedbackSort` union AND the backend `sort` enum in feedback.ts.
const VALID_SORTS: readonly FeedbackSort[] = [
  'triage',
  'newest',
  'oldest',
  'best',
  'latency_asc',
  'latency_desc',
  'cost_asc',
  'cost_desc',
];

const VALID_SOURCES: readonly FeedbackSource[] = ['all', 'chat', 'brief'];

const DEFAULT_SORT: FeedbackSort = 'triage';
const DEFAULT_LIMIT = 50;
const DEFAULT_OFFSET = 0;
const DEFAULT_SOURCE: FeedbackSource = 'all';

export interface FeedbackUrlState {
  sort: FeedbackSort;
  limit: number;
  offset: number;
  minRating?: number;
  maxRating?: number;
  hasText?: boolean;
  userId?: string;
  /** K2 — feedback source partition (all / chat / brief). */
  source: FeedbackSource;
  /**
   * Phase 5 (dashboard-trace-ux) — trace IDs whose detail panels are
   * inline-expanded on the FeedbackPage. Empty array → no rows expanded
   * (URL omits the `expanded` key). Multi-row expand is allowed; capped
   * at `EXPANDED_MAX` entries on write.
   */
  expanded: string[];
}

// ---------------------------------------------------------------------------
// Parse helpers
// ---------------------------------------------------------------------------

/** Coerce `raw` → integer within `[min, max]`, else `fallback`. */
function parseIntInRange(
  raw: string | null,
  min: number,
  max: number,
  fallback: number,
): number {
  if (raw === null) return fallback;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  if (n < min || n > max) return fallback;
  return n;
}

/** Integer in `[min, max]` if parsable, else `undefined` (optional). */
function parseOptionalInt(
  raw: string | null,
  min: number,
  max: number,
): number | undefined {
  if (raw === null) return undefined;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return undefined;
  if (n < min || n > max) return undefined;
  return n;
}

/** `'true'` → true, `'false'` → false, anything else → undefined. */
function parseBool(raw: string | null): boolean | undefined {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

/** Known FeedbackSort if recognised, else `fallback`. */
function parseSort(raw: string | null): FeedbackSort {
  if (raw && (VALID_SORTS as readonly string[]).includes(raw)) {
    return raw as FeedbackSort;
  }
  return DEFAULT_SORT;
}

/** Known FeedbackSource if recognised, else `DEFAULT_SOURCE`. */
function parseSource(raw: string | null): FeedbackSource {
  if (raw && (VALID_SOURCES as readonly string[]).includes(raw)) {
    return raw as FeedbackSource;
  }
  return DEFAULT_SOURCE;
}

/** `userId` must match the same regex the backend enforces — else drop. */
const USER_ID_RE = /^[A-Za-z0-9._-]+$/;
function parseUserId(raw: string | null): string | undefined {
  if (!raw) return undefined;
  if (raw.length > 64) return undefined;
  if (!USER_ID_RE.test(raw)) return undefined;
  return raw;
}

/**
 * Phase 5 — Langfuse trace IDs are alphanumeric with optional `_` / `-`. We
 * accept the same charset the rest of the dashboard (e.g. langfuseTraceUrl)
 * already URL-encodes through, but reject anything that looks like a path or
 * query injector. Per-id length cap matches Langfuse's typical 32-char IDs
 * with margin; the array length cap (`EXPANDED_MAX`) is enforced separately
 * on write so a deep-linked URL with 100 IDs gets clamped on read too.
 */
const TRACE_ID_RE = /^[A-Za-z0-9_-]+$/;
function parseExpanded(raw: string | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const candidate of raw.split(',')) {
    const id = candidate.trim();
    if (!id) continue;
    if (id.length > 64) continue;
    if (!TRACE_ID_RE.test(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= EXPANDED_MAX) break;
  }
  return out;
}

function readFromSearch(params: URLSearchParams): FeedbackUrlState {
  return {
    sort: parseSort(params.get('sort')),
    limit: parseIntInRange(params.get('limit'), 1, 200, DEFAULT_LIMIT),
    offset: parseIntInRange(
      params.get('offset'),
      0,
      Number.MAX_SAFE_INTEGER,
      DEFAULT_OFFSET,
    ),
    minRating: parseOptionalInt(params.get('minRating'), 1, 5),
    maxRating: parseOptionalInt(params.get('maxRating'), 1, 5),
    hasText: parseBool(params.get('hasText')),
    userId: parseUserId(params.get('userId')),
    source: parseSource(params.get('source')),
    expanded: parseExpanded(params.get('expanded')),
  };
}

// ---------------------------------------------------------------------------
// Write helpers
// ---------------------------------------------------------------------------

/**
 * P2-4 (v1.4-dashboard-ssot): clamp/drop out-of-range rating values at the
 * WRITE layer. Previously the input field accepted any number (type="number"
 * with no constraint), and `commitFeedback({ minRating: 99 })` happily
 * serialised `?minRating=99` to the URL; the input then re-read via
 * `parseOptionalInt` dropped it back to undefined (value clamp on read), so
 * the visible field reverted to empty while the URL kept the invalid param.
 * The user saw "nothing happened" — and the dirty URL leaked into history.
 *
 * Reject out-of-range values at write time (match the read-side clamp to
 * `[1, 5]`). `undefined` still clears the param.
 */
function isValidRating(n: number | undefined): boolean {
  if (n === undefined) return true;
  if (!Number.isFinite(n)) return false;
  return n >= 1 && n <= 5 && Number.isInteger(n);
}

/** Apply `next` to `params` in place, dropping default-valued / undefined
 *  entries so the URL stays short. Does NOT touch non-feedback keys. */
function writeStateToParams(
  params: URLSearchParams,
  next: FeedbackUrlState,
): void {
  // sort
  if (next.sort === DEFAULT_SORT) {
    params.delete('sort');
  } else {
    params.set('sort', next.sort);
  }
  // limit
  if (next.limit === DEFAULT_LIMIT) {
    params.delete('limit');
  } else {
    params.set('limit', String(next.limit));
  }
  // offset
  if (next.offset === DEFAULT_OFFSET) {
    params.delete('offset');
  } else {
    params.set('offset', String(next.offset));
  }
  // optional fields — P2-4: drop out-of-range rating values rather than
  // persisting a ?minRating=99 into the URL. The read-side clamp already
  // ignores them, so the UX is "invalid input silently reverts" instead of
  // "URL now carries a dead param".
  if (next.minRating === undefined || !isValidRating(next.minRating)) {
    params.delete('minRating');
  } else {
    params.set('minRating', String(next.minRating));
  }
  if (next.maxRating === undefined || !isValidRating(next.maxRating)) {
    params.delete('maxRating');
  } else {
    params.set('maxRating', String(next.maxRating));
  }
  if (next.hasText === undefined) {
    params.delete('hasText');
  } else {
    params.set('hasText', next.hasText ? 'true' : 'false');
  }
  if (next.userId === undefined) {
    params.delete('userId');
  } else {
    params.set('userId', next.userId);
  }
  // K2 — source partition. Default is 'all' → drop from URL.
  if (next.source === DEFAULT_SOURCE) {
    params.delete('source');
  } else {
    params.set('source', next.source);
  }
  // Phase 5 — expanded trace IDs. Empty array → drop the key. Cap and
  // dedupe at the WRITE side too so callers can't sneak past the read clamp
  // (e.g. by passing the raw user list straight from a parent setter).
  if (next.expanded.length === 0) {
    params.delete('expanded');
  } else {
    const seen = new Set<string>();
    const clean: string[] = [];
    for (const id of next.expanded) {
      if (typeof id !== 'string') continue;
      if (!TRACE_ID_RE.test(id) || id.length > 64) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      clean.push(id);
      if (clean.length >= EXPANDED_MAX) break;
    }
    if (clean.length === 0) {
      params.delete('expanded');
    } else {
      params.set('expanded', clean.join(','));
    }
  }
}

// ---------------------------------------------------------------------------
// Structural equality — isolated from useUrlFilters.filtersEqual
// ---------------------------------------------------------------------------

function stateEqual(a: FeedbackUrlState, b: FeedbackUrlState): boolean {
  if (a === b) return true;
  if (a.sort !== b.sort) return false;
  if (a.limit !== b.limit) return false;
  if (a.offset !== b.offset) return false;
  if (a.minRating !== b.minRating) return false;
  if (a.maxRating !== b.maxRating) return false;
  if (a.hasText !== b.hasText) return false;
  if (a.userId !== b.userId) return false;
  if (a.source !== b.source) return false;
  if (a.expanded.length !== b.expanded.length) return false;
  for (let i = 0; i < a.expanded.length; i++) {
    if (a.expanded[i] !== b.expanded[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseFeedbackUrlStateResult extends FeedbackUrlState {
  /** Pending buffer — bound to controls; only commits to URL on `apply()`. */
  pending: FeedbackUrlState;
  /** Replace the whole pending buffer (typed setter — preserves not-set semantics). */
  setFilters: (patch: Partial<FeedbackUrlState>) => void;
  /** Single-field setters kept for ergonomic bindings. */
  setSort: (s: FeedbackSort) => void;
  setLimit: (n: number) => void;
  /**
   * Commit a new `offset` to the URL immediately (bypass pending buffer) —
   * used by the Load-more button where the Apply-button workflow would feel
   * laggy.
   */
  setOffset: (n: number) => void;
  /**
   * Phase 5 — commit a new `expanded` array (capped, deduped) directly to
   * the URL. Used by FeedbackTable's row toggle. Bypasses the pending
   * buffer (rows expand/collapse instantly) and uses `replace: true` so
   * we don't pollute the back/forward stack on every chevron click.
   * Other URL keys are preserved verbatim.
   */
  setExpanded: (next: string[]) => void;
  /**
   * K1 — live-apply entry point: merge `patch` onto the CURRENT committed
   * state and flush to the URL immediately, bypassing the pending buffer.
   * Mirrors `useUrlFilters.setPending` so Feedback-tab controls behave the
   * same as the global FilterBar (Bug F1). `offset` is reset to 0 unless the
   * patch sets it explicitly — any filter change restarts pagination.
   */
  commit: (patch: Partial<FeedbackUrlState>) => void;
  /** Flush `pending` to the URL. */
  apply: () => void;
  /** Clear the 7 feedback keys from the URL, leaving global filter keys intact. */
  reset: () => void;
  /** True when pending !== committed. Isolated from useUrlFilters.dirty. */
  dirty: boolean;
}

export function useFeedbackUrlState(): UseFeedbackUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Committed state — derived from the current URL on every render. We read
  // from `searchParams` (react-router's URLSearchParams clone) rather than
  // `location.search` because URLSearchParams is already decoded and there
  // are no comma-array semantics for feedback params.
  const committed = useMemo<FeedbackUrlState>(
    () => readFromSearch(searchParams),
    [searchParams],
  );

  const [pending, setPending] = useState<FeedbackUrlState>(committed);

  // Resync pending to committed when the URL changes underneath us (back
  // button, setSearchParams from another hook, …). Keyed on the toString()
  // of the current params so only genuine URL mutations trigger the reset
  // (the `committed` reference changes every render otherwise).
  const paramsKey = searchParams.toString();
  useEffect(() => {
    setPending(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const setFilters = useCallback((patch: Partial<FeedbackUrlState>) => {
    setPending((prev) => ({ ...prev, ...patch }));
  }, []);

  const setSort = useCallback(
    (s: FeedbackSort) => setFilters({ sort: s }),
    [setFilters],
  );
  const setLimit = useCallback(
    (n: number) => setFilters({ limit: n }),
    [setFilters],
  );

  const apply = useCallback(() => {
    // Functional form — preserves useUrlFilters' keys (role, cohort, …)
    // because we only mutate the 7 feedback keys via writeStateToParams.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        writeStateToParams(next, pending);
        return next;
      },
      { replace: false },
    );
  }, [pending, setSearchParams]);

  const setOffset = useCallback(
    (n: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Commit directly onto the CURRENT committed state, not pending —
          // Load-more should NOT flush any other pending edits.
          writeStateToParams(next, { ...committed, offset: n });
          return next;
        },
        { replace: false },
      );
    },
    [committed, setSearchParams],
  );

  // Phase 5 — `setExpanded`. Uses `replace: true` so a triage session that
  // toggles 8 rows in 30s doesn't leave an 8-deep back stack the user must
  // mash through to leave the page. Only the `expanded` key is rewritten;
  // every other param (filters, sort, offset…) survives verbatim because
  // we mutate a clone of `prev` and only touch one key in the writer.
  const setExpanded = useCallback(
    (nextExpanded: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          writeStateToParams(next, { ...committed, expanded: nextExpanded });
          return next;
        },
        { replace: true },
      );
    },
    [committed, setSearchParams],
  );

  // K1 — live-apply commit. Merges `patch` onto the CURRENT committed state
  // (not pending) and flushes straight to the URL. Using `committed` as the
  // base means rapid successive edits always layer onto the freshest URL
  // snapshot, mirroring `useUrlFilters.setPending`. `offset` defaults back
  // to 0 on any filter/sort change — Load-more still uses `setOffset()`.
  const commit = useCallback(
    (patch: Partial<FeedbackUrlState>) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          const nextState: FeedbackUrlState = {
            ...committed,
            offset: 0,
            ...patch,
          };
          writeStateToParams(nextParams, nextState);
          return nextParams;
        },
        { replace: false },
      );
    },
    [committed, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of FEEDBACK_URL_KEYS) next.delete(key);
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const dirty = useMemo(
    () => !stateEqual(committed, pending),
    [committed, pending],
  );

  return {
    ...committed,
    pending,
    setFilters,
    setSort,
    setLimit,
    setOffset,
    setExpanded,
    commit,
    apply,
    reset,
    dirty,
  };
}
